import { useRef, useState } from 'react';
import {
  useCreateAsset,
  useAssetSerialDuplicate,
  ASSET_CATEGORY_LABELS,
  AssetCategory,
} from '@/hooks/useAssets';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Camera, Loader2, AlertTriangle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

interface AssetCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (assetId: string) => void;
}

// User-pickable categories. Intentionally excludes `lift_bay` and
// `flat_bay` from the picker — those rows are created automatically by
// the ensure_bay_has_asset trigger when a shop_bay is added (see the
// footnote in the dialog body). Editing those is done via the Bays
// surface, not by adding a new asset here. All other 17 values from
// ASSET_CATEGORY_VALUES are picker-eligible.
const categoryOrder: AssetCategory[] = [
  // Scheduling/bay-shape equipment
  'alignment', 'ac_machine', 'tire_machine', 'wheel_balancer',
  // Diagnostic
  'scan_tool', 'diagnostic_equipment', 'adas',
  // Service / fluid / power
  'fluid_service_machine', 'battery_equipment', 'compressor',
  // Fabrication
  'welder', 'press',
  // Tooling / catch-alls
  'specialty_tooling', 'shop_equipment',
  // IT / software
  'computer_equipment', 'software_subscription',
  // Fallback
  'other',
];

/**
 * Response shape from supabase/functions/parse-equipment-tag/index.ts
 * (Batch 3). Generalized counterpart of parse-lift-tag — same first
 * four fields plus assetType + extraNotes. The function never
 * auto-saves; we always return parsed values for the user to review.
 *
 * parse-lift-tag continues to be used by the Facility Editor's lift
 * inspector; OptiAssets uses this generalized function so we can scan
 * non-lift equipment (scan tools, laptops, presses, etc.).
 */
interface ParseEquipmentTagResponse {
  success?: boolean;
  parsed?: {
    brand: string | null;
    modelNumber: string | null;
    serialNumber: string | null;
    capacityLbs: number | null;
    assetType: string | null;
    extraNotes: string | null;
  };
  error?: string;
}

export function AssetCreateDialog({ open, onOpenChange, onSuccess }: AssetCreateDialogProps) {
  const createAsset = useCreateAsset();

  const [name, setName] = useState('');
  const [category, setCategory] = useState<AssetCategory>('other');
  const [assetType, setAssetType] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');
  const [serial, setSerial] = useState('');
  const [isPortable, setIsPortable] = useState(false);
  const [locationText, setLocationText] = useState('');
  const [notes, setNotes] = useState('');

  // OCR prefill state (Batch 2). The scan path is OPTIONAL — manual
  // creation works exactly as before, and a failed scan leaves the
  // form usable.
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanSummary, setScanSummary] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Soft duplicate check on serial (advisory only — the DB has no
  // unique constraint on serial, so we never hard-block).
  const { data: duplicate } = useAssetSerialDuplicate(serial);

  const resetForm = () => {
    setName('');
    setCategory('other');
    setAssetType('');
    setManufacturer('');
    setModel('');
    setSerial('');
    setIsPortable(false);
    setLocationText('');
    setNotes('');
    setScanError(null);
    setScanSummary(null);
  };

  const handleSubmit = async () => {
    const result = await createAsset.mutateAsync({
      name,
      category,
      asset_type: assetType.trim() || undefined,
      manufacturer: manufacturer || undefined,
      model: model || undefined,
      serial: serial || undefined,
      is_portable: isPortable,
      home_location_text: locationText || undefined,
      notes: notes || undefined,
    });

    resetForm();
    onOpenChange(false);
    onSuccess?.(result.id);
  };

  /**
   * OCR equipment-nameplate scan flow.
   *
   *  1. Convert selected image to base64 (data URL — the edge function
   *     strips the data: prefix internally).
   *  2. POST to parse-equipment-tag (Gemini 2.5 Flash on the image bytes).
   *     This function is the generalized counterpart of parse-lift-tag:
   *     it returns the same brand / model / serial / capacityLbs fields,
   *     plus an `assetType` free-text field and an optional `extraNotes`
   *     line. parse-lift-tag stays in use by the Facility Editor's lift
   *     inspector — OptiAssets uses the generalized function so we can
   *     scan scan tools, presses, lab scopes, laptops, etc.
   *  3. Prefill manufacturer / model / serial / assetType. Append
   *     "Capacity: X lbs" and any extraNotes to notes (no dedicated
   *     capacity column exists on shop_assets).
   *  4. NEVER auto-submits. User must review and click Create.
   */
  const handleScanFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      const msg = 'Please choose an image file';
      setScanError(msg);
      toast.error(msg);
      return;
    }

    setScanError(null);
    setScanSummary(null);
    setIsScanning(true);

    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Failed to read image file'));
        reader.readAsDataURL(file);
      });

      const { data, error } = await supabase.functions.invoke<ParseEquipmentTagResponse>(
        'parse-equipment-tag',
        { body: { imageBase64: base64 } },
      );

      if (error) throw error;
      if (!data?.success || !data.parsed) {
        throw new Error(data?.error || 'Could not parse nameplate from image');
      }

      const parsed = data.parsed;
      const filled: string[] = [];

      if (parsed.brand) {
        setManufacturer(parsed.brand);
        filled.push('manufacturer');
      }
      if (parsed.modelNumber) {
        setModel(parsed.modelNumber);
        filled.push('model');
      }
      if (parsed.serialNumber) {
        setSerial(parsed.serialNumber);
        filled.push('serial');
      }
      if (parsed.assetType) {
        setAssetType(parsed.assetType);
        filled.push('asset type');
      }

      // Capacity + extra notes are appended into the free-text Notes
      // field rather than typed columns (no capacity column today; the
      // Batch 3 schema deliberately keeps the typed surface narrow).
      const noteAdditions: string[] = [];
      if (parsed.capacityLbs && Number.isFinite(parsed.capacityLbs)) {
        noteAdditions.push(`Capacity: ${parsed.capacityLbs.toLocaleString()} lbs`);
        filled.push('capacity (added to notes)');
      }
      if (parsed.extraNotes) {
        noteAdditions.push(parsed.extraNotes);
        filled.push('extra notes');
      }
      if (noteAdditions.length) {
        const block = noteAdditions.join('\n');
        setNotes(prev => (prev ? `${prev}\n${block}` : block));
      }

      const summary = filled.length
        ? `Nameplate parsed — prefilled ${filled.join(', ')}. Review the fields before creating the asset.`
        : 'Nameplate parsed, but no recognizable fields were detected. Please enter details manually.';
      setScanSummary(summary);
      if (filled.length) {
        toast.success('Nameplate parsed — review fields before creating', {
          icon: <Sparkles className="h-4 w-4" />,
        });
      } else {
        toast.message('Nameplate parsed but no fields were extracted');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not parse nameplate';
      setScanError(msg);
      toast.error('Could not parse nameplate', { description: msg });
    } finally {
      setIsScanning(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const isValid = name.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) resetForm(); onOpenChange(o); }}>
      {/* max-h + overflow keeps "Create Asset" reachable on iPad landscape
          / with the keyboard up, when the long form (scan + fields +
          duplicate warning + notes) exceeds the viewport height. */}
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Asset</DialogTitle>
          <DialogDescription>
            Register a new piece of equipment for tracking.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Optional OCR nameplate scan. Manual creation works unchanged
              if the user ignores this control. Works for any equipment
              nameplate / spec plate — lifts, scan tools, presses, A/C
              machines, laptops, etc. */}
          <div className="rounded-md border border-dashed p-3 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm">
                <div className="font-medium">Scan nameplate (optional)</div>
                <p className="text-xs text-muted-foreground">
                  Upload a photo of any equipment nameplate or spec plate to auto-fill manufacturer, model, serial, and type.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isScanning}
                onClick={() => fileInputRef.current?.click()}
                aria-label="Scan nameplate photo"
              >
                {isScanning ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Parsing…
                  </>
                ) : (
                  <>
                    <Camera className="h-4 w-4 mr-2" />
                    Scan
                  </>
                )}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleScanFile(file);
                }}
              />
            </div>
            {scanSummary && (
              <p className="text-xs text-muted-foreground" role="status">
                {scanSummary}
              </p>
            )}
            {scanError && (
              <p className="text-xs text-destructive flex items-start gap-1.5" role="alert">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>{scanError}. You can still enter details manually below.</span>
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              placeholder="e.g., Hunter Alignment Rack, Bay 4 Compressor, SA Laptop"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Anything the shop wants to track: lifts, scan tools, lab scopes, tire machines, presses, spring compressors, computers — anything.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as AssetCategory)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categoryOrder.map(cat => (
                    <SelectItem key={cat} value={cat}>
                      {ASSET_CATEGORY_LABELS[cat]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Bay assets are created automatically when bays are added.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="asset-type">Equipment Type</Label>
              <Input
                id="asset-type"
                placeholder='e.g., "PicoScope 4425A"'
                value={assetType}
                onChange={(e) => setAssetType(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Optional free-text — more specific than category.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="manufacturer">Manufacturer</Label>
              <Input
                id="manufacturer"
                placeholder="e.g., Hunter"
                value={manufacturer}
                onChange={(e) => setManufacturer(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="model">Model</Label>
              <Input
                id="model"
                placeholder="e.g., HawkEye Elite"
                value={model}
                onChange={(e) => setModel(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="serial">Serial Number</Label>
            <Input
              id="serial"
              placeholder="e.g., HE-2024-12345"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              className="font-mono"
            />
            {duplicate && (
              <p className="text-xs text-amber-600 dark:text-amber-400 flex items-start gap-1.5" role="alert">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>
                  An asset named "{duplicate.name}" already has this serial in your shop.
                  You can still create this asset if it's intentional.
                </span>
              </p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="portable">Portable Equipment</Label>
              <p className="text-xs text-muted-foreground">Can be moved between bays</p>
            </div>
            <Switch
              id="portable"
              checked={isPortable}
              onCheckedChange={setIsPortable}
            />
          </div>

          {isPortable && (
            <div className="space-y-2">
              <Label htmlFor="location">Home Location</Label>
              <Input
                id="location"
                placeholder="e.g., Tool room, Bay 3 area"
                value={locationText}
                onChange={(e) => setLocationText(e.target.value)}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Any additional details..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || createAsset.isPending}>
            {createAsset.isPending ? 'Creating...' : 'Create Asset'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
